import { useState } from "react";
import { api } from "../../../lib/api";
import { fmtDT, fmtINR } from "../../../lib/constants";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Prohibit, WarningCircle, CircleNotch, ArrowClockwise, Receipt } from "@phosphor-icons/react";
import { Panel, Info, friendlyError } from "./_shared";

// AIR12-CXL — Order Cancellation & Manual Refund UI (Commit 3).
// All commercial values, stage and eligibility come from the backend. The frontend never
// recalculates amounts, GST, stages, cash or the preview fingerprint. Internal fields
// (calc_evidence, UUIDs, Super Admin reason, processor identity) are never rendered to the Sender.

const STAGE_LABEL = {
  PRE_PAYMENT: "Before payment",
  TIER_1: "Before pickup vehicle assigned",
  TIER_2: "Pickup vehicle assigned",
  TIER_3: "Picked up — before source receipt",
  TIER_4: "Source receipt complete — before airline handover",
};
const REFUND_STATUS_LABEL = {
  REFUND_PENDING: "Refund pending",
  REFUND_COMPLETED: "Refund completed",
  NO_REFUND_DUE: "No refund due",
};

function errCode(e) {
  const detail = e?.response?.data?.detail;
  return detail && typeof detail === "object" ? detail.code : undefined;
}

export function CancellationPanel({ d, reload, profile }) {
  const s = d.shipment;
  const st = d.cancellation_settlement || null;
  const role = profile?.role;
  const isSender = role === "SENDER";
  const isSuperAdmin = role === "SUPER_ADMIN";
  const isAssignedAccounts = role === "ACCOUNTS" && s.accounts_user_id === profile?.id;
  const isSourceCtop = role === "CTOP" && s.source_ctop_id === profile?.id;
  const isCtrl = role === "CTRL";  // M3 — airport-related CTRL (page is backend-gated) may READ

  // Only these roles ever see any cancellation/refund surface. Backend enforces the same.
  // Read visibility includes any airport-related CTRL; action controls below stay SA/Source-CTOP only.
  const canSee = isSender || isSuperAdmin || isSourceCtop || isCtrl;
  if (!canSee) return null;

  const cancelled = s.current_status === "CANCELLED";
  const handedOver = !!s.airline_handover_done;
  const canInitiate = (isSender || isSuperAdmin) && !cancelled && !handedOver;

  if (cancelled) {
    return isSender
      ? <SenderTerminalPanel st={st} />
      : <OpsTerminalPanel st={st} d={d} reload={reload} canProcess={isSuperAdmin || isAssignedAccounts} isSuperAdmin={isSuperAdmin} />;
  }
  if (canInitiate) return <InitiateCancellation d={d} reload={reload} isSuperAdmin={isSuperAdmin} />;
  return null;
}

// ---------------------------------------------------------------------------
// Sender / Super Admin — initiate cancellation (preview → confirm with fingerprint)
// ---------------------------------------------------------------------------
function InitiateCancellation({ d, reload, isSuperAdmin }) {
  const s = d.shipment;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonErr, setReasonErr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stale, setStale] = useState(false);
  const [deltaState, setDeltaState] = useState(null);

  const loadPreview = async () => {
    setLoading(true); setPreviewErr(false);
    try {
      const { data } = await api.get(`/booking/${s.id}/cancellation-preview`);
      setPreview(data);
    } catch (e) {
      setPreview(null); setPreviewErr(true);
    } finally { setLoading(false); }
  };

  const openModal = async () => {
    setOpen(true); setStale(false); setReason(""); setReasonErr(false); setDeltaState(null);
    // Best-effort DELTA state — drives the post-airport (TIER_4) dynamic confirmation copy.
    try { const { data } = await api.get(`/checkout/${s.id}/delta`); setDeltaState(data); } catch (e) { /* best-effort */ }
    await loadPreview();
  };

  const closeModal = () => {
    if (submitting) return;
    setOpen(false); setPreview(null); setStale(false); setReason(""); setReasonErr(false); setDeltaState(null);
  };

  const confirmCancel = async () => {
    if (!preview?.eligible || !preview?.fingerprint) return;
    if (isSuperAdmin && !reason.trim()) { setReasonErr(true); return; }
    setSubmitting(true); setStale(false); setReasonErr(false);
    try {
      const body = { fingerprint: preview.fingerprint };
      if (isSuperAdmin) body.reason = reason.trim();
      const { data } = await api.post(`/booking/${s.id}/cancel`, body);
      toast.success(data.message || "Order cancelled.");
      setOpen(false); setPreview(null);
      reload();
    } catch (e) {
      if (errCode(e) === "CANCELLATION_PREVIEW_STALE") {
        setStale(true);
        await loadPreview(); // refresh values + fingerprint; force explicit re-confirmation
      } else {
        toast.error(friendlyError(e, "Could not cancel this order."));
      }
    } finally { setSubmitting(false); }
  };

  const eligible = preview?.eligible;
  const prePayment = preview?.stage === "PRE_PAYMENT";
  const cashMode = preview?.stage === "TIER_4" || preview?.cash_mode;
  // Post-source-receipt (TIER_4) dynamic confirmation copy, keyed on DELTA state.
  const postAirport = preview?.stage === "TIER_4";
  const dAmt = Number(deltaState?.delta_amount || 0);
  const dFinalized = !!deltaState?.finalized;
  const dSettled = !!deltaState?.settled;
  const dPaid = dSettled && deltaState?.current_attempt?.status === "COMPLETED";
  let postAirportMsg = null;
  if (postAirport) {
    if (dFinalized && dAmt > 0 && !dSettled) {
      postAirportMsg = `No refund is available. ${fmtINR(dAmt)} balance remains payable. Air12 will arrange the return separately. Cancel shipment?`;
    } else if (dPaid) {
      postAirportMsg = "No refund is available, including the balance already paid. Air12 will arrange the return separately. Cancel shipment?";
    } else {
      postAirportMsg = "No refund is available after airport arrival. Air12 will arrange the return separately. Cancel shipment?";
    }
  }

  return (
    <Panel icon={<Prohibit size={16} weight="bold" />} title="ORDER CANCELLATION">
      <div className="space-y-2" data-testid="cancellation-initiate-panel">
        <p className="text-xs text-[hsl(var(--muted))]">
          You can cancel this order until it is handed over to the airline. Applicable Air12
          charges are retained and any remaining amount is refunded.
        </p>
        <button
          className="btn-ghost text-sm text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.4)]"
          onClick={openModal}
          data-testid="order-cancellation-button"
        >
          <Prohibit size={14} weight="bold" /> Order Cancellation
        </button>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : closeModal())}>
        <DialogContent className="max-w-md" data-testid="cancellation-modal">
          <DialogHeader>
            <DialogTitle>Cancel order?</DialogTitle>
            <DialogDescription>
              Cancellation takes effect immediately and cannot be reversed. Air12 will stop
              processing this shipment.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-6 flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted))]" data-testid="cancellation-preview-loading">
              <CircleNotch size={18} className="animate-spin" /> Calculating cancellation details…
            </div>
          ) : previewErr ? (
            <div className="py-4 space-y-3" data-testid="cancellation-preview-error">
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--danger))]">
                <WarningCircle size={18} /> We couldn’t load the cancellation details.
              </div>
              <button className="btn-secondary text-sm" onClick={loadPreview} data-testid="cancellation-preview-retry">
                <ArrowClockwise size={14} /> Retry
              </button>
            </div>
          ) : !eligible ? (
            <div className="py-4" data-testid="cancellation-unavailable">
              <p className="text-sm text-[hsl(var(--muted))]">
                {preview?.reason || "This order can no longer be cancelled."}
              </p>
              <div className="flex justify-end mt-4">
                <button className="btn-secondary text-sm" onClick={closeModal} data-testid="cancellation-unavailable-close">Close</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="cancellation-preview-body">
              {stale && (
                <div className="px-3 py-2 rounded bg-[hsl(var(--warning)/0.12)] border border-[hsl(var(--warning)/0.4)] text-xs" data-testid="cancellation-stale-note">
                  The cancellation calculation has changed. Please review the updated amount before confirming.
                </div>
              )}

              {postAirportMsg && (
                <div className="px-3 py-2 rounded bg-[hsl(var(--danger)/0.10)] border border-[hsl(var(--danger)/0.4)] text-xs font-medium" data-testid="cancellation-postairport-warning">
                  {postAirportMsg}
                </div>
              )}

              {prePayment ? (
                <div className="px-3 py-2 rounded bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-sm" data-testid="cancellation-prepayment-note">
                  No Air12 payment has been collected. The order will be cancelled immediately.
                </div>
              ) : cashMode ? (
                <div className="grid-card p-3 text-sm" data-testid="cancellation-cashmode-note">
                  <div className="overline mb-2">Air12 will retain</div>
                  <p className="text-[hsl(var(--muted))]">
                    Your shipment has reached the source airport. The full amount already paid is
                    retained and <span className="font-semibold">no refund is due</span>.
                  </p>
                  <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex items-center justify-between">
                    <span className="font-semibold">Amount retained</span>
                    <span className="heading text-lg mono" data-testid="cxl-cash-retained">{fmtINR(preview.actual_amount_retained)}</span>
                  </div>
                </div>
              ) : (
                <div className="grid-card p-3 text-sm" data-testid="cancellation-retained-breakdown">
                  <div className="overline mb-2">Air12 will retain</div>
                  {preview.source_local_movement > 0 && (
                    <Row k="Pickup transport" v={preview.source_local_movement} testid="cxl-source-movement" />
                  )}
                  {preview.pickup_load_assist > 0 && (
                    <Row k="Loading Help — Pickup Side" v={preview.pickup_load_assist} testid="cxl-pickup-load-assist" />
                  )}
                  {preview.core_custody_retained > 0 && (
                    <Row k="Core Custody" v={preview.core_custody_retained} testid="cxl-core-custody" />
                  )}
                  {preview.cancellation_charge > 0 && (
                    <Row k="Cancellation charge" v={preview.cancellation_charge} testid="cxl-cancellation-charge" />
                  )}
                  {preview.concierge_service_fee > 0 && (
                    <Row k="Concierge Service (non-refundable)" v={preview.concierge_service_fee} testid="cxl-concierge-service" />
                  )}
                  {preview.forfeited_insurance_premium > 0 && (
                    <Row k="Transit insurance (non-refundable)" v={preview.forfeited_insurance_premium} testid="cxl-forfeited-insurance" />
                  )}
                  <Row k={`GST on retained charges (${Number(preview.frozen_gst_rate || 18)}%)`} v={preview.retained_gst} testid="cxl-retained-gst" />
                  <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex items-center justify-between">
                    <span className="font-semibold">Refund due</span>
                    <span className="heading text-lg mono" data-testid="cxl-refund-amount">{fmtINR(preview.refund_amount)}</span>
                  </div>
                </div>
              )}

              {preview.insurance_forfeited && (
                <p className="text-xs text-[hsl(var(--warning))]" data-testid="cancellation-insurance-note">
                  Any transit insurance premium you paid is non-refundable once your payment is completed.
                </p>
              )}

              {isSuperAdmin && (
                <div className="space-y-1" data-testid="cancellation-admin-reason-block">
                  <label className="overline">Reason for cancelling on behalf of Sender</label>
                  <textarea
                    className={`input-field w-full h-20 ${reasonErr ? "border-[hsl(var(--danger))]" : ""}`}
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); if (reasonErr) setReasonErr(false); }}
                    data-testid="cancellation-admin-reason-input"
                  />
                  <p className="text-[11px] text-[hsl(var(--muted))]">
                    This internal reason is retained in the audit history and is not shown to the Sender.
                  </p>
                  {reasonErr && <p className="text-[11px] text-[hsl(var(--danger))]" data-testid="cancellation-admin-reason-error">A reason is required.</p>}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button className="btn-secondary text-sm" disabled={submitting} onClick={closeModal} data-testid="cancellation-keep-order">Keep order</button>
                <button
                  className="btn-primary text-sm bg-[hsl(var(--danger))] border-[hsl(var(--danger))]"
                  disabled={submitting}
                  onClick={confirmCancel}
                  data-testid="cancellation-confirm-button"
                >
                  {submitting ? "Cancelling…" : "Cancel order"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex items-center justify-between py-1" data-testid={testid}>
      <span className="text-[hsl(var(--muted))]">{k}</span>
      <span className="mono">{fmtINR(v)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sender terminal states (after cancellation)
// ---------------------------------------------------------------------------
function SenderTerminalPanel({ st }) {
  // No settlement row → cancelled before any Air12 payment was collected.
  let title, body, status;
  if (!st) {
    status = "Cancelled";
    title = "Order cancelled";
    body = "This order was cancelled before any Air12 payment was collected.";
  } else if (st.status === "REFUND_PENDING") {
    status = REFUND_STATUS_LABEL.REFUND_PENDING;
    title = "Order cancelled";
    body = `Order cancelled. A manual refund of ${fmtINR(st.refund_amount)} is being processed.`;
  } else if (st.status === "REFUND_COMPLETED") {
    status = REFUND_STATUS_LABEL.REFUND_COMPLETED;
    title = "Order cancelled";
    body = `Order cancelled. Your refund of ${fmtINR(st.refund_amount)} has been completed.`;
  } else {
    status = "Cancelled — no refund due";
    title = "Order cancelled";
    body = "Order cancelled. No refund is due.";
  }

  return (
    <Panel icon={<Prohibit size={16} weight="bold" />} title="ORDER CANCELLED">
      <div className="space-y-2 text-sm" data-testid="sender-cancellation-terminal">
        <div className="flex items-center gap-2">
          <span className="status-pill status-info" data-testid="sender-refund-status">{status}</span>
        </div>
        <p data-testid="sender-cancellation-message">{body}</p>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Operations terminal (Source CTOP / Source Controller / Super Admin)
// ---------------------------------------------------------------------------
function OpsTerminalPanel({ st, d, reload, canProcess, isSuperAdmin }) {
  const [open, setOpen] = useState(false);
  if (!st) {
    return (
      <Panel icon={<Prohibit size={16} weight="bold" />} title="ORDER CANCELLED">
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="ops-cancellation-prepayment">
          This order was cancelled before any Air12 payment was collected. No settlement is due.
        </p>
      </Panel>
    );
  }
  const pending = st.status === "REFUND_PENDING";
  const completed = st.status === "REFUND_COMPLETED";
  return (
    <Panel icon={<Prohibit size={16} weight="bold" />} title="ORDER CANCELLED — SETTLEMENT">
      <div className="space-y-3 text-sm" data-testid="ops-cancellation-terminal">
        <div className="flex items-center justify-between">
          <Info label="STAGE">{STAGE_LABEL[st.stage] || st.stage}</Info>
          <span className="status-pill status-info" data-testid="ops-refund-status">{REFUND_STATUS_LABEL[st.status] || st.status}</span>
        </div>

        <div className="grid-card p-3">
          <div className="overline mb-2">SETTLEMENT</div>
          <Row k="Completed Air12 cash" v={st.total_completed_cash} testid="ops-completed-cash" />
          {st.cash_mode ? (
            <p className="text-xs text-[hsl(var(--muted))] py-1" data-testid="ops-cashmode-note">
              Source receipt complete — full paid consideration retained (no refund).
            </p>
          ) : (
            <>
              {st.source_local_movement_retained > 0 && (
                <Row k="Pickup transport retained" v={st.source_local_movement_retained} testid="ops-source-movement" />
              )}
              {st.pickup_load_assist_retained > 0 && (
                <Row k="Loading Help — Pickup Side retained" v={st.pickup_load_assist_retained} testid="ops-pickup-load-assist" />
              )}
              {st.core_custody_retained > 0 && (
                <Row k="Core Custody retained" v={st.core_custody_retained} testid="ops-core-custody" />
              )}
              {st.cancellation_charge > 0 && (
                <Row k="Cancellation charge" v={st.cancellation_charge} testid="ops-cancellation-charge" />
              )}
              {st.concierge_service_fee > 0 && (
                <Row k="Concierge Service retained (non-refundable)" v={st.concierge_service_fee} testid="ops-concierge-service" />
              )}
              {st.forfeited_insurance_premium > 0 && (
                <Row k="Transit insurance retained (non-refundable)" v={st.forfeited_insurance_premium} testid="ops-forfeited-insurance" />
              )}
            </>
          )}
          <Row k="GST retained" v={st.retained_gst} testid="ops-retained-gst" />
          <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex items-center justify-between">
            <span className="font-semibold">Refund amount</span>
            <span className="heading text-lg mono" data-testid="ops-refund-amount">{fmtINR(st.refund_amount)}</span>
          </div>
        </div>

        {completed && (
          <div className="grid-card p-3" data-testid="ops-refund-completed-block">
            <div className="overline mb-2">REFUND COMPLETED</div>
            <div className="grid grid-cols-2 gap-2">
              {st.processed_at && <Info label="PROCESSED"><span className="mono">{fmtDT(st.processed_at)}</span></Info>}
            </div>
          </div>
        )}

        {pending && canProcess && (
          <button
            className="btn-primary text-sm"
            onClick={() => setOpen(true)}
            data-testid="process-manual-refund-button"
          >
            <Receipt size={14} weight="bold" /> Process manual refund
          </button>
        )}
      </div>

      {pending && canProcess && (
        <ManualRefundModal open={open} onClose={() => setOpen(false)} st={st} shipmentId={d.shipment.id} reload={reload} />
      )}
    </Panel>
  );
}

function ManualRefundModal({ open, onClose, st, shipmentId, reload }) {
  const [submitting, setSubmitting] = useState(false);

  const close = () => { if (submitting) return; onClose(); };

  const submit = async () => {
    setSubmitting(true);
    try {
      // One-click confirmation only — refund amount is server-frozen; no UTR/reference/note/amount.
      await api.post(`/booking/${shipmentId}/cancellation/refund-complete`, {});
      toast.success("Refund marked processed.");
      close();
      reload();
    } catch (e) {
      toast.error(friendlyError(e, "Could not complete the refund."));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : close())}>
      <DialogContent className="max-w-md" data-testid="manual-refund-modal">
        <DialogHeader>
          <DialogTitle>Confirm refund processed?</DialogTitle>
          <DialogDescription>
            Confirm that the refund of {fmtINR(st.refund_amount)} has been processed (money moved
            manually off-app). This action is permanent and cannot be corrected in the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid-card p-3" data-testid="manual-refund-amount">
            <div className="overline">REFUND AMOUNT</div>
            <div className="heading text-lg mono mt-0.5">{fmtINR(st.refund_amount)}</div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn-secondary text-sm" disabled={submitting} onClick={close} data-testid="manual-refund-cancel">Go back</button>
            <button className="btn-primary text-sm" disabled={submitting} onClick={submit} data-testid="manual-refund-confirm">
              {submitting ? "Processing…" : "Refund processed"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
